import { useEffect, useState } from 'react';
import { social, type ProfileDto } from '../net/api';
import { wearFrom } from '../art/avatar';
import { AvatarPreview } from './AvatarPreview';
import { Sheet } from './Sheet';
import { BadgeRow } from './Badges';

const CATEGORIES: Record<string, string> = {
  unsafe: 'Made me feel unsafe (asked to meet up, or to share private things)',
  harassment: 'Bullying or harassment',
  language: 'Bad language',
  personal_info: 'Asking for or sharing personal information',
  scam: 'Scamming',
  inappropriate_name: 'Inappropriate name',
  cheating: 'Cheating',
  other: 'Something else',
};

export function ProfileCard({ name, catalog, onClose }: { name: string; catalog: Map<string, { render: Record<string, unknown> }>; onClose: () => void }) {
  const [p, setP] = useState<ProfileDto>();
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const [reporting, setReporting] = useState(false);
  const [category, setCategory] = useState('harassment');
  const [details, setDetails] = useState('');

  const load = () => social.profile(name).then(setP, (e: Error) => setMsg({ kind: 'err', text: e.message }));
  useEffect(() => {
    void load();
  }, [name]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      setMsg({ kind: 'ok', text: ok });
      await load();
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  };

  return (
    <Sheet title={p?.displayName ?? name} subtitle={p ? `Level ${p.level}${p.memberSince ? ` · in Buddy Bay since ${p.memberSince}` : ''}` : undefined} onClose={onClose}>
      {p && (
        <div className="profile">
          <div className="profile-look">
            <AvatarPreview look={{ characterType: p.characterType, species: p.species, appearance: p.appearance, wear: wearFrom(p.equipped, catalog) }} scale={1.6} />
          </div>
          <div className="stack">
            {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
            {p.verified && <p className="verified-mark" title="This account is verified">✔ Verified</p>}
            {p.badges.length > 0 && <BadgeRow badges={p.badges} />}
            {p.relation === 'friend' && <p className="tag">Friends</p>}
            <div className="row-wrap">
              {p.relation === 'none' && <button className="btn" onClick={() => void act(() => social.request(p.displayName), 'Friend request sent.')}>Add friend</button>}
              {p.relation === 'requested_you' && <button className="btn" onClick={() => void act(() => social.request(p.displayName), "You're now friends!")}>Accept friend request</button>}
              {p.relation === 'requested' && <span className="muted">Friend request sent</span>}
              {p.relation === 'friend' && <button className="btn ghost" onClick={() => void act(() => social.unfriend(p.displayName), 'Removed from friends.')}>Remove friend</button>}
              {p.relation === 'blocked' ? (
                <button className="btn ghost" onClick={() => void act(() => social.unblock(p.displayName), 'Unblocked.')}>Unblock</button>
              ) : (
                p.relation !== 'self' && <button className="btn ghost" onClick={() => void act(() => social.block(p.displayName), "Blocked. You won't see each other's messages.")}>Block</button>
              )}
              {p.relation !== 'self' && <button className="btn ghost" onClick={() => setReporting(!reporting)}>Report</button>}
            </div>
            {reporting && (
              <form className="report-form" onSubmit={(e) => { e.preventDefault(); void act(() => social.report(p.displayName, category, details || undefined), 'Thanks. Our team will look into it.').then(() => setReporting(false)); }}>
                <label htmlFor="report-cat">What happened?</label>
                <select id="report-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <label htmlFor="report-details">Anything else? (optional)</label>
                <textarea id="report-details" rows={2} maxLength={500} value={details} onChange={(e) => setDetails(e.target.value)} />
                <p className="muted small-print">Their recent messages are attached automatically.</p>
                <button className="btn">Send report</button>
              </form>
            )}
          </div>
        </div>
      )}
    </Sheet>
  );
}
